const transitioningStates = new WeakSet()

export function markAuthTransitioning(state) {
  transitioningStates.add(state)
}

export function isAuthTransitioning(state) {
  return transitioningStates.has(state)
}

export function getAuthIdentity(state = {}) {
  const user = state.user_info || {}
  const id = user.pk ?? user.id ?? user.user_id ?? user.email
  if (id === undefined || id === null || id === '') return ''
  return `${user.user_type || ''}:${String(id)}`
}

export function isCurrentAuthSession(state, identity, storage) {
  if (!identity || isAuthTransitioning(state) || getAuthIdentity(state) !== identity) return false
  try {
    const auth = readStoredAuth(storage, state.auth_refresh_mode)
    return Boolean(auth && getAuthIdentity(auth) === identity && auth.auth_refresh_mode === state.auth_refresh_mode)
  } catch {
    return false
  }
}

function readStoredAuth(storage, defaultMode) {
  const raw = storage.getItem('auth')
  if (raw === null) return { auth_refresh_mode: defaultMode, user_info: {} }
  const parsed = JSON.parse(raw)
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null
  if (!parsed.user_info || typeof parsed.user_info !== 'object' || Array.isArray(parsed.user_info)) return null
  const auth = {
    auth_refresh_mode: parsed.auth_refresh_mode === 'cookie' ? 'cookie' : 'legacy',
    user_info: parsed.user_info,
    userEmailInput_remembered: parsed.userEmailInput_remembered || '',
  }
  if (auth.auth_refresh_mode !== 'cookie' || auth.user_info.user_type === 'google') {
    for (const key of ['access_token', 'refresh_token', 'id_token']) {
      auth[key] = typeof parsed[key] === 'string' ? parsed[key] : ''
    }
  }
  return auth
}

export function createAuthStorageListener({ store, storage, createInitialState, navigate, onSessionChange, onProfileChange }) {
  let transitioning = false
  return event => {
    if (transitioning || (event.key !== 'auth' && event.key !== null)) return
    if (event.storageArea && event.storageArea !== storage) return
    let auth
    try {
      // Queued storage events may already have been superseded by another login.
      auth = readStoredAuth(storage, store.state.auth_refresh_mode)
    } catch {
      return
    }
    if (!auth) return

    const currentIdentity = getAuthIdentity(store.state)
    const nextIdentity = getAuthIdentity(auth)
    const sessionChanged = currentIdentity !== nextIdentity
      || store.state.auth_refresh_mode !== auth.auth_refresh_mode
      || (!nextIdentity && Boolean(store.state.access_token || store.state.refresh_token || store.state.id_token))
      || (event.newValue === null && Boolean(currentIdentity))

    if (sessionChanged) {
      transitioning = true
      markAuthTransitioning(store.state)
      // Destroy the old view before replacing state, including its beforeunload
      // handlers. A canceled browser navigation must not leave that view active.
      onSessionChange?.()
      const cleanState = createInitialState(auth.auth_refresh_mode)
      if (!nextIdentity) {
        auth.access_token = ''
        auth.refresh_token = ''
        auth.id_token = ''
      }
      const nextState = { ...cleanState, ...auth }
      markAuthTransitioning(nextState)
      store.replaceState(nextState)
      // Do not commit logout here: persistence subscribers would broadcast it back
      // and could overwrite the account that just logged in in the originating tab.
      storage.removeItem('recolumn')
      onProfileChange?.(auth.user_info)
      // A new document disposes old component state, timers and pending callbacks.
      navigate(nextIdentity ? '/workspace' : '/')
      return
    }

    store.replaceState({ ...store.state, ...auth })
    onProfileChange?.(auth.user_info)
  }
}
